/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from 'react';

import { Spinner } from '@aicon-dev/caas-ui';
import Measure from 'react-measure';

import './forge-viewer.css';
import { LoadScript } from '~/components/common/LoadScript';

import { getForgeviewerToken } from './libs/utils';

interface ForgeViewerProps {
  urn?: string | string[];
  view?: any;
  proxy?: string;
  headless?: boolean;
  version?: string;
  onTokenRequest: (callback: (token: string, expire: number) => void) => void;
  onModelLoad?: (viewer: any, model: any) => void;
  onModelError?: (errorCode: number) => void;
  onViewerError?: (errorCode: number) => void;
  onDocumentLoad?: (doc: any, views: any[]) => void;
  onDocumentError?: (errorCode: number) => void;
}

export const ForgeViewer = (props: ForgeViewerProps) => {
  const [enable, setEnable] = useState(false);
  const [error, setError] = useState(false);
  const [empty, setEmpty] = useState(true);
  const viewerDiv = useRef<HTMLDivElement>(null);
  const viewer = useRef<any>(null);
  const docs = useRef<string[]>([]);
  const views = useRef<{ [key: string]: any }>({});
  const resizeHandling = useRef<NodeJS.Timeout | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (props.urn) {
      if (Array.isArray(props.urn)) {
        docs.current.push(...props.urn);
      } else {
        docs.current.push(props.urn);
      }
    }

    return () => {
      console.log('React Forge Viewer unmounting...');
      if (viewer.current) {
        viewer.current.tearDown();
        viewer.current.finish();
        viewer.current = null;
        console.log('React Forge Viewer destroyed.');
      }
    };
  }, [props.urn]);

  const handleLoadModelSuccess = useCallback(
    (model: any) => {
      console.log('Model successfully loaded by Forge Viewer.', model);
      viewer.current.setSwapBlackAndWhite(true);
      if (props.onModelLoad) props.onModelLoad(viewer.current, model);
    },
    [props.onModelLoad],
  );

  const handleLoadModelError = useCallback(
    (errorCode: number) => {
      setError(true);
      console.error('Error loading Forge model - errorCode:', errorCode);
      if (props.onModelError) props.onModelError(errorCode);
    },
    [props.onModelError],
  );

  const handleViewerError = useCallback(
    (errorCode: number) => {
      setError(true);
      console.error('Error loading Forge Viewer. - errorCode:', errorCode);
      if (props.onViewerError) props.onViewerError(errorCode);
    },
    [props.onViewerError],
  );

  const handleScriptLoad = () => {
    console.log('Autodesk scripts have finished loading.');

    const options = {
      env: 'AutodeskProduction',
      getAccessToken: getForgeviewerToken,
    };

    if (initialized) {
      console.log('viewer Initialized');
      return;
    }

    window.Autodesk.Viewing.Initializer(options, handleViewerInit);
  };

  const handleViewerInit = useCallback(() => {
    console.log('Forge Viewer has finished initializing.');
    setInitialized(true);

    if (props.proxy) {
      window.Autodesk.Viewing.endpoint.setEndpointAndApi(props.proxy, 'derivativeV2');
    }

    const container = viewerDiv.current;

    const config3d = {
      extensions: [
        'Autodesk.DocumentBrowser',
        'Autodesk.Viewing.Wireframes',
        'Autodesk.ViewerSettings',
        'Autodesk.LayerManager',
        'Autodesk.ViewCubeUi',
      ],
      modelBrowserExcludeRoot: false,
    };

    if (!container) return;

    if (props.headless) {
      viewer.current = new window.Autodesk.Viewing.Viewer3D(container);
    } else {
      viewer.current = new window.Autodesk.Viewing.GuiViewer3D(container, config3d);
    }

    console.log('Starting the Forge Viewer...');
    const errorCode = viewer.current.start();
    if (!errorCode) {
      console.log('Forge Viewer has successfully started.');
      setEnable(true);
      reviewDocuments();
    } else {
      console.error('Error starting Forge Viewer - code:', errorCode);
      handleViewerError(errorCode);
    }
  }, [props.proxy, props.headless, handleViewerError]);

  const handleLoadDocumentSuccess = useCallback(
    (doc: any) => {
      console.log('Forge viewer has successfully loaded document:', doc);

      const viewables = doc.getRoot().search({ type: 'geometry' }, true);
      viewables.forEach((viewable: any) => {
        viewable.doc = doc;
      });

      if (props.onDocumentLoad) props.onDocumentLoad(doc, viewables);
    },
    [props.onDocumentLoad],
  );

  const handleLoadDocumentError = useCallback(
    (errorCode: number) => {
      setError(true);
      console.error('Error loading Forge document - errorCode:', errorCode);
      if (props.onDocumentError) props.onDocumentError(errorCode);
    },
    [props.onDocumentError],
  );

  const clearErrors = useCallback(() => {
    setError(false);
  }, []);

  // const clearViews = useCallback(() => {
  //   console.log('clearing all views.');
  //   views.current = {};
  //   if (viewer.current) {
  //     viewer.current.tearDown();
  //     viewer.current.start();
  //   }
  // }, []);

  const reviewDocuments = useCallback(() => {
    if (viewer.current) {
      clearErrors();
      console.log('reviewing documents...');
      setEmpty(docs.current.length === 0);
      docs.current.forEach((urn) => {
        loadDocument(urn);
      });
    }
  }, [clearErrors]);

  const loadDocument = useCallback(
    (urn: string) => {
      console.log('Forge Viewer is loading document:', urn);

      const documentId = `urn:${urn}`;
      const successHandler = handleLoadDocumentSuccess;
      const errorHandler = handleLoadDocumentError;

      window.Autodesk.Viewing.Document.load(documentId, successHandler, errorHandler);
    },
    [handleLoadDocumentSuccess, handleLoadDocumentError],
  );

  const loadView = useCallback(
    (view: any) => {
      console.log('loading view:', view.guid);
      views.current[view.viewableID] = view;

      const svfUrl = view.doc.getViewablePath(view);
      const successHandler = handleLoadModelSuccess;
      const errorHandler = handleLoadModelError;
      const modelOptions = {
        sharedPropertyDbPath: view.doc.getFullPath(),
        isAEC: true,
      };

      viewer.current?.loadModel(svfUrl, modelOptions, successHandler, errorHandler);
    },
    [handleLoadModelSuccess, handleLoadModelError],
  );

  // const isArrayDifferent = useCallback((current: any[], next: any[]): boolean => {
  //   if (current == null && next == null) return false;
  //   else if (current == null || next == null) return true;
  //   else if (current.length !== next.length) return true;

  //   for (let i = 0; i < current.length; i++) if (current[i] !== next[i]) return true;
  //   return false;
  // }, []);

  // const setDocuments = useCallback(
  //   (list: string[]) => {
  //     docs.current = list;
  //     clearViews();
  //     reviewDocuments();
  //   },
  //   [clearViews, reviewDocuments],
  // );

  const setViews = useCallback(
    (list: any[]) => {
      const existing = { ...views.current };
      const incremental: any[] = [];
      list.forEach((view) => {
        // eslint-disable-next-line no-prototype-builtins
        if (existing.hasOwnProperty(view.viewableID)) {
          delete existing[view.viewableID];
        } else {
          incremental.push(view);
        }
      });

      const keys = Object.keys(existing);
      if (keys.length > 0) {
        viewer.current.tearDown();
        viewer.current.start();
        list.forEach((view) => {
          loadView(view);
        });
      } else {
        incremental.forEach((view) => {
          loadView(view);
        });
      }
    },
    [loadView],
  );

  // useEffect(() => {
  //   if (props.urn) {
  //     setDocuments(Array.isArray(props.urn) ? props.urn : [props.urn]);
  //   }
  // }, [props.urn]);

  useEffect(() => {
    if (props.view) {
      setViews(Array.isArray(props.view) ? props.view : [props.view]);
    }
  }, [props.view]);

  const handleResize = useCallback(() => {
    if (resizeHandling.current) clearTimeout(resizeHandling.current);

    resizeHandling.current = setTimeout(() => {
      if (viewer.current) viewer.current.resize();
    }, 100);
  }, []);

  // useEffect(() => {
  //   shouldComponentUpdateURN(props);
  //   shouldComponentUpdateView(props);
  // }, [props, shouldComponentUpdateURN, shouldComponentUpdateView]);
  console.log('render render >>> ');
  const version = props.version || '7.19';

  return (
    <Measure bounds onResize={handleResize}>
      {({ measureRef }) => (
        <div ref={measureRef} className="ForgeViewer">
          <div ref={viewerDiv}></div>
          <link
            rel="stylesheet"
            type="text/css"
            href={`https://developer.api.autodesk.com/modelderivative/v2/viewers/style.min.css?v=v${version}`}
          />
          <LoadScript
            src={`https://developer.api.autodesk.com/modelderivative/v2/viewers/viewer3D.min.js?v=v${version}`}
            onLoad={handleScriptLoad}
            onError={() => handleViewerError(0)}
          />

          <link
            rel="stylesheet"
            type="text/css"
            href={`http://cdn.jsdelivr.net/gh/autodesk-forge/forge-extensions/public/extensions/NestedViewerExtension/contents/main.css`}
          />
          <LoadScript
            src={`http://cdn.jsdelivr.net/gh/autodesk-forge/forge-extensions/public/extensions/NestedViewerExtension/contents/main.js`}
            onLoad={() => console.log('nested view ext have finished loading.')}
            onError={() => console.log('nested view ext load failed.')}
          />

          {empty && (
            <div className="scrim">
              <div className="message">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12.89 1.45l8 4A2 2 0 0 1 22 7.24v9.53a2 2 0 0 1-1.11 1.79l-8 4a2 2 0 0 1-1.79 0l-8-4a2 2 0 0 1-1.1-1.8V7.24a2 2 0 0 1 1.11-1.79l8-4a2 2 0 0 1 1.78 0z"></path>
                  <polyline points="2.32 6.16 12 11 21.68 6.16"></polyline>
                  <line x1="12" y1="22.76" x2="12" y2="11"></line>
                </svg>
              </div>
            </div>
          )}

          {error && (
            <div className="scrim">
              <div className="message">
                <div
                  style={{
                    padding: '35px',
                  }}
                >
                  <Spinner />
                </div>
                <div style={{ color: 'black' }}>파일을 변환 중입니다. 잠시 후에 이용해 주세요.</div>
                <div>(새로고침 또는 파일을 다시 선택 해주세요.)</div>
              </div>
            </div>
          )}

          {!enable && (
            <div className="scrim">
              <div className="message">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
                </svg>
                <div>Starting Viewer...</div>
              </div>
            </div>
          )}
        </div>
      )}
    </Measure>
  );
};
